import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  createBudgetLineItemSetting,
  deleteBudgetLineItemSetting,
  getChartOfAccountsSettings,
  getBudgetLineItemCategories,
  getBudgetLineItemsSettings,
  getChartOfAccountsLookup,
  updateBudgetLineItemSetting,
} from "../api/financeApi";
import { getApiErrorMessage } from "../utils/concurrency";

const emptyLineForm = {
  id: null,
  name: "",
  category: "",
  default_account_code: "",
  is_active: true,
};

export default function BudgetLineItemsSettingsPage() {
  const [rows, setRows] = useState([]);
  const [accountCodes, setAccountCodes] = useState([]);
  const [allAccountCodes, setAllAccountCodes] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyLineForm);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [linesRes, codesRes, allCodesRes, categoriesRes] = await Promise.all([
        getBudgetLineItemsSettings(),
        getChartOfAccountsLookup(),
        getChartOfAccountsSettings(),
        getBudgetLineItemCategories(),
      ]);
      setRows(linesRes.data.results || []);
      setAccountCodes(codesRes.data.results || []);
      setAllAccountCodes(allCodesRes.data.results || []);
      setCategoryOptions(categoriesRes.data.results || []);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load budget line items."));
    } finally {
      setLoading(false);
    }
  }, []);

  const categoryHint = useMemo(() => {
    const typed = form.category.trim();
    if (!typed) return null;
    const exact = categoryOptions.find(
      (option) => option.toLowerCase() === typed.toLowerCase()
    );
    if (exact && exact !== typed) {
      return `Will be saved as "${exact}" to match an existing category.`;
    }
    const similar = categoryOptions.find(
      (option) =>
        option.toLowerCase() !== typed.toLowerCase() &&
        (option.toLowerCase().includes(typed.toLowerCase()) ||
          typed.toLowerCase().includes(option.toLowerCase()))
    );
    if (similar) {
      return `Similar category exists: "${similar}". Select it from the list to avoid duplicates.`;
    }
    return null;
  }, [form.category, categoryOptions]);

  const inactiveAccountCodes = useMemo(
    () =>
      new Set(
        allAccountCodes
          .filter((account) => !account.is_active)
          .map((account) => account.code)
      ),
    [allAccountCodes]
  );
  const inactiveAccountLineCount = rows.filter(
    (row) => row.default_account_code && inactiveAccountCodes.has(row.default_account_code)
  ).length;
  const categoryGroups = useMemo(() => {
    const groups = new Map();
    rows.forEach((row) => {
      const label = row.category || "Uncategorised";
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(row);
    });
    return Array.from(groups, ([label, items]) => ({ label, items }));
  }, [rows]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyLineForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name,
      category: row.category || "",
      default_account_code: row.default_account_code || "",
      is_active: row.is_active,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        name: form.name.trim(),
        category: form.category.trim() || null,
        default_account_code: form.default_account_code || null,
        is_active: form.is_active,
      };
      if (form.id) {
        await updateBudgetLineItemSetting(form.id, payload);
      } else {
        await createBudgetLineItemSetting(payload);
      }
      setDialogOpen(false);
      setToast({ open: true, message: "Budget line item saved.", severity: "success" });
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to save budget line item."));
    } finally {
      setSaving(false);
    }
  };

  const deleteLineItem = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      setError("");
      await deleteBudgetLineItemSetting(deleteTarget.id);
      setDeleteTarget(null);
      setToast({ open: true, message: "Budget line item deleted.", severity: "success" });
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to delete budget line item."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Catalogue of lines pickable when configuring Estate Charges and Block Charges on each property
        budget.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {inactiveAccountLineCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {inactiveAccountLineCount} budget line item{inactiveAccountLineCount === 1 ? "" : "s"} reference an inactive account code.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Budget line items</Typography>
          <Button variant="contained" size="small" onClick={openCreate}>
            Add line item
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Default account</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4}>Loading…</TableCell>
                </TableRow>
              ) : (
                categoryGroups.map((group) => (
                  <React.Fragment key={group.label}>
                    <TableRow>
                      <TableCell colSpan={4} sx={{ bgcolor: "action.hover", fontWeight: 700 }}>
                        {group.label}
                      </TableCell>
                    </TableRow>
                    {group.items.map((row) => {
                      const hasInactiveAccount =
                        row.default_account_code && inactiveAccountCodes.has(row.default_account_code);
                      return (
                        <TableRow key={row.id} sx={{ opacity: row.is_active ? 1 : 0.55 }}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>
                            {row.default_account_code || "—"}
                            {hasInactiveAccount && (
                              <Chip size="small" color="warning" label="Inactive account" sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.is_active ? "Active" : "Inactive"}
                              color={row.is_active ? "success" : "default"}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => openEdit(row)}
                              >
                                Edit
                              </Button>
                              {row.can_delete && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => setDeleteTarget(row)}
                                >
                                  Delete
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? "Edit budget line item" : "Add budget line item"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <Autocomplete
            freeSolo
            options={categoryOptions}
            value={form.category || null}
            inputValue={form.category}
            onInputChange={(_, value) => setForm((f) => ({ ...f, category: value }))}
            onChange={(_, value) => setForm((f) => ({ ...f, category: value || "" }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                helperText={
                  categoryHint ||
                  "Pick an existing category or type a new one. Most-used categories appear first."
                }
              />
            )}
          />
          <FormControl>
            <InputLabel>Default account code</InputLabel>
            <Select
              label="Default account code"
              value={form.default_account_code}
              onChange={(e) =>
                setForm((f) => ({ ...f, default_account_code: e.target.value }))
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {accountCodes.map((account) => (
                <MenuItem key={account.code} value={account.code}>
                  {account.code} — {account.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {form.id && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !saving && setDeleteTarget(null)}>
        <DialogTitle>Delete budget line item?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete {deleteTarget?.name}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteLineItem} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
